/**
 * Form utility functions for dev-tools.
 *
 * Pure functions for form state management, field validation, prefilling
 * from organisation context, and building request payloads.
 */
import { FieldSchema, FormState, ValidationRule } from "../types";
import { logger } from "./logger";

/**
 * Creates an empty form state with default values
 */
export const createEmptyFormState = (): FormState => ({
  data: {},
  errors: {},
  touched: {},
  isValid: true,
  isSubmitting: false,
});

/**
 * Updates a specific field in the form state
 */
export const updateFormField = (
  formState: FormState,
  fieldName: string,
  value: unknown,
): FormState => ({
  ...formState,
  data: {
    ...formState.data,
    [fieldName]: value,
  },
  touched: {
    ...formState.touched,
    [fieldName]: true,
  },
});

/**
 * Sets form errors and updates validity status
 */
export const setFormErrors = (
  formState: FormState,
  errors: Record<string, string>,
): FormState => ({
  ...formState,
  errors,
  isValid: Object.keys(errors).length === 0,
});

/**
 * Resets form to initial state with optional initial data
 */
export const resetForm = (
  initialData?: Record<string, unknown>,
): FormState => ({
  data: initialData || {},
  errors: {},
  touched: {},
  isValid: true,
  isSubmitting: false,
});

/**
 * Sets form submission state
 */
export const setFormSubmitting = (
  formState: FormState,
  isSubmitting: boolean,
): FormState => ({
  ...formState,
  isSubmitting,
});

/**
 * Marks a field as touched
 */
export const markFieldTouched = (
  formState: FormState,
  fieldName: string,
): FormState => ({
  ...formState,
  touched: {
    ...formState.touched,
    [fieldName]: true,
  },
});

/**
 * Gets the number of errors in the form
 */
export const getErrorCount = (formState: FormState): number => {
  return Object.keys(formState.errors).length;
};

/**
 * Checks if a specific field has an error
 */
export const hasFieldError = (
  formState: FormState,
  fieldName: string,
): boolean => {
  return !!formState.errors[fieldName] && !!formState.touched[fieldName];
};

/**
 * Gets the error message for a specific field
 */
export const getFieldError = (
  formState: FormState,
  fieldName: string,
): string | undefined => {
  return formState.touched[fieldName] ? formState.errors[fieldName] : undefined;
};

/**
 * Validates a single field based on its schema with comprehensive error handling
 * @param field - The field schema definition
 * @param value - The value to validate
 * @returns Error message if validation fails, undefined if valid
 */
export const validateField = (
  field: FieldSchema,
  value: unknown,
): string | undefined => {
  try {
    // Input sanitization and null checks
    if (!field) {
      logger.formDebug("validateField called with null/undefined field");
      return "Invalid field configuration";
    }

    if (!field.name) {
      logger.formDebug("validateField called with field missing name");
      return "Invalid field name";
    }

    // Check required fields
    if (
      !field.isOptional &&
      (value === null || value === undefined || value === "")
    ) {
      return `${field.name} is required`;
    }

    // Skip validation for optional empty fields
    if (
      field.isOptional &&
      (value === null || value === undefined || value === "")
    ) {
      return undefined;
    }

    // Type validation with enhanced checks
    if (value !== null && value !== undefined && value !== "") {
      switch (field.type) {
        case "string":
          if (typeof value !== "string") {
            return `${field.name} must be a string`;
          }
          break;
        case "number":
        case "int32":
        case "uint32":
        case "float":
          if (typeof value !== "number" || isNaN(value)) {
            return `${field.name} must be a valid number`;
          }
          // Additional range checks for specific integer types
          if (field.type === "uint32" && value < 0) {
            return `${field.name} must be a positive number`;
          }
          if (
            field.type === "int32" &&
            (value < -2147483648 || value > 2147483647)
          ) {
            return `${field.name} must be within int32 range`;
          }
          break;
        case "boolean":
          if (typeof value !== "boolean") {
            return `${field.name} must be a boolean`;
          }
          break;
        case "enum":
          if (field.enumValues && Array.isArray(field.enumValues)) {
            const isValidEnum = field.enumValues.some(
              (e) => e.value === value || e.name === value,
            );
            if (!isValidEnum) {
              const validOptions = field.enumValues
                .map((e) => e.name)
                .slice(0, 3)
                .join(", ");
              const moreCount = Math.max(0, field.enumValues.length - 3);
              const optionsText =
                moreCount > 0
                  ? `${validOptions} and ${moreCount} more`
                  : validOptions;
              return `${field.name} must be one of: ${optionsText}`;
            }
          } else {
            logger.formDebug(
              `Enum field ${field.name} has no enumValues defined`,
            );
          }
          break;
        case "message":
          if (typeof value === "string") {
            try {
              JSON.parse(value);
            } catch (jsonError) {
              return `${field.name} must be valid JSON`;
            }
          } else if (typeof value !== "object") {
            return `${field.name} must be an object or valid JSON`;
          }
          break;
      }
    }

    // Custom validation rules with error handling
    if (field.validation && Array.isArray(field.validation)) {
      for (const rule of field.validation) {
        try {
          const error = validateRule(rule, value, field.name);
          if (error) {
            return error;
          }
        } catch (ruleError) {
          logger.error(`Error in validation rule for ${field.name}`, {
            ruleError,
          });
          return `Validation error in ${field.name}`;
        }
      }
    }

    return undefined;
  } catch (error) {
    logger.error(
      `Unexpected error validating field ${field?.name || "unknown"}`,
      { error },
    );
    return `Validation error in ${field?.name || "field"}`;
  }
};

/**
 * Validates a single validation rule
 */
const validateRule = (
  rule: ValidationRule,
  value: unknown,
  fieldName: string,
): string | undefined => {
  switch (rule.type) {
    case "required":
      if (value === null || value === undefined || value === "") {
        return rule.message || `${fieldName} is required`;
      }
      break;
    case "email":
      if (
        typeof value === "string" &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      ) {
        return rule.message || `${fieldName} must be a valid email`;
      }
      break;
    case "url":
      if (typeof value === "string") {
        try {
          new URL(value);
        } catch {
          return rule.message || `${fieldName} must be a valid URL`;
        }
      }
      break;
    case "pattern":
      if (typeof value === "string" && typeof rule.value === "string") {
        const regex = new RegExp(rule.value);
        if (!regex.test(value)) {
          return rule.message || `${fieldName} format is invalid`;
        }
      }
      break;
    case "range":
      if (typeof value === "number" && typeof rule.value === "object") {
        const range = rule.value as { min?: number; max?: number };
        if (range.min !== undefined && value < range.min) {
          return rule.message || `${fieldName} must be at least ${range.min}`;
        }
        if (range.max !== undefined && value > range.max) {
          return rule.message || `${fieldName} must be at most ${range.max}`;
        }
      }
      break;
  }
  return undefined;
};

/**
 * Creates a default value for a field based on its schema
 */
export const createDefaultValue = (field: FieldSchema): unknown => {
  if (field.defaultValue !== undefined) {
    return field.defaultValue;
  }

  if (field.isOptional) {
    return undefined;
  }

  switch (field.type) {
    case "string":
      return "";
    case "number":
    case "int32":
    case "uint32":
    case "float":
      return 0;
    case "boolean":
      return false;
    case "enum":
      return field.enumValues?.[0]?.value || 0;
    case "message":
      return {};
    case "repeated":
      return [];
    default:
      return undefined;
  }
};

/**
 * Creates a field mapping from orgData for prefilling forms
 */
export const createOrgDataFieldMap = (
  orgData: unknown,
): Record<string, unknown> => {
  if (!orgData || typeof orgData !== "object") {
    return {};
  }

  if (!("id" in orgData)) {
    return {};
  }

  // Type assertion for orgData access based on actual Role structure
  const orgDataObj = orgData as {
    id: string;
    members?: string[];
    info?: {
      email?: string;
      phone?: string;
      address?: {
        street?: string;
        city?: string;
        country?: string;
      };
    };
    subscription?: Array<{
      type?: string;
      active?: boolean;
      id?: string;
      components?: {
        userSeats?: number;
      };
      chargify?: {
        currency?: string;
      };
    }>;
  };

  const fieldMap = {
    // Primary role/org ID mappings
    roleId: orgDataObj.id,
    orgId: orgDataObj.id,
    organisationId: orgDataObj.id,
    organizationId: orgDataObj.id,

    // User ID from members array
    userId:
      Array.isArray(orgDataObj.members) && orgDataObj.members.length > 0
        ? orgDataObj.members[0]
        : undefined,

    // Contact information (if available)
    email: orgDataObj.info?.email || undefined,
    phone: orgDataObj.info?.phone || undefined,

    // Subscription information (if available)
    subscriptionType: orgDataObj.subscription?.[0]?.type || undefined,
    subscriptionActive: orgDataObj.subscription?.[0]?.active || undefined,
    subscriptionId: orgDataObj.subscription?.[0]?.id || undefined,
    userSeats: orgDataObj.subscription?.[0]?.components?.userSeats || undefined,
    currency: orgDataObj.subscription?.[0]?.chargify?.currency || undefined,

    // Common pagination
    pageSize: 15,
  };

  return fieldMap;
};

/**
 * Detects fields that can be prefilled from orgData
 */
export const detectPrefillableFields = (
  schema: FieldSchema[],
  fieldMap: Record<string, unknown>,
): FieldSchema[] => {
  return schema.filter((field) => {
    const value = fieldMap[field.name];
    return value !== undefined && value !== null && value !== "";
  });
};

/**
 * Prefills form state with orgData values (enhanced with nested field support)
 */
export const prefillFormWithOrgData = (
  formState: FormState,
  orgData: unknown,
  schema: FieldSchema[],
): FormState => {
  // Skip if no orgData available
  if (!orgData || typeof orgData !== "object" || !("id" in orgData)) {
    logger.formDebug("orgData or orgData.id is missing, skipping prefill");
    return formState;
  }

  try {
    // Prefill only the first layer (top-level fields). Nested message fields
    // are left untouched — prefilling into nested messages guessed values by
    // matching leaf field names across the whole tree, which was surprising
    // (e.g. an org id landing inside an unrelated nested filter).
    const finalState = prefillFlatFields(formState, orgData, schema);

    logger.formDebug("Form prefilling completed", {
      originalData: formState.data,
      prefilledData: finalState.data,
    });

    return finalState;
  } catch (error) {
    logger.error("Error prefilling form with orgData", { error });
    return formState;
  }
};

/**
 * Original flat field prefilling logic (kept for backwards compatibility)
 */
const prefillFlatFields = (
  formState: FormState,
  orgData: unknown,
  schema: FieldSchema[],
): FormState => {
  try {
    // Create field mapping from orgData
    const fieldMap = createOrgDataFieldMap(orgData);

    // Detect prefillable fields in schema
    const prefillableFields = detectPrefillableFields(schema, fieldMap);

    // Create prefilled data
    const prefilledData = { ...formState.data };

    // Apply prefill values
    prefillableFields.forEach((field) => {
      const value = fieldMap[field.name];
      if (value !== undefined && value !== null && value !== "") {
        prefilledData[field.name] = value;
      }
    });

    // Return updated form state
    return {
      ...formState,
      data: prefilledData,
    };
  } catch (error) {
    logger.error("Error in flat field prefilling", { error });
    return formState;
  }
};

/**
 * Checks if a field was prefilled from orgData
 */
export const isFieldPrefilled = (
  fieldName: string,
  orgData: unknown,
): boolean => {
  if (!orgData || typeof orgData !== "object" || !("id" in orgData)) {
    return false;
  }

  const fieldMap = createOrgDataFieldMap(orgData);
  return (
    fieldMap[fieldName] !== undefined &&
    fieldMap[fieldName] !== null &&
    fieldMap[fieldName] !== ""
  );
};

/**
 * Builds a clean request object from form data, filtering out empty values.
 *
 * If `schema` is supplied, only fields whose schema type is "message" (or
 * "repeated") get their string values JSON-parsed — this avoids silently
 * turning a regular string that happens to start with `{` or `[` into an
 * object. Without `schema`, no auto-parse is attempted.
 */
export const buildRequestObject = (
  formData: Record<string, unknown>,
  schema?: FieldSchema[],
): Record<string, unknown> => {
  const requestData: Record<string, unknown> = {};
  const jsonFieldNames = new Set(
    (schema ?? [])
      .filter((field) => field.type === "message" || field.isRepeated)
      .map((field) => field.name),
  );

  Object.entries(formData).forEach(([key, value]) => {
    // 0 is a valid value (important for enums); exclude only null/undefined/"".
    if (value === null || value === undefined || value === "") return;

    if (typeof value === "string" && jsonFieldNames.has(key)) {
      try {
        requestData[key] = JSON.parse(value);
      } catch {
        requestData[key] = value;
      }
    } else {
      requestData[key] = value;
    }
  });

  return requestData;
};

/**
 * A oneof member's value as constructed by the message class, e.g.
 * `{ case: "quota", value: { userRestriction: { limit: 5 } } }`. protobuf-es
 * requires this shape for oneof fields — a flat `{ quota: {...} }` sibling
 * key is silently dropped by the message constructor instead of erroring.
 */
export interface OneofValue {
  case: string;
  value: unknown;
}

/**
 * Splits a message's fields into ordinary fields and oneof groups, keyed by
 * oneofGroup name. Fields sharing a oneofGroup are mutually exclusive
 * alternatives (protobuf oneof members), not independent fields, and must be
 * rendered/serialized as a single selectable case rather than side by side.
 */
export const groupFieldsByOneof = (
  fields: FieldSchema[] | undefined,
): {
  plainFields: FieldSchema[];
  oneofGroups: Record<string, FieldSchema[]>;
} => {
  const plainFields: FieldSchema[] = [];
  const oneofGroups: Record<string, FieldSchema[]> = {};

  (fields ?? []).forEach((f) => {
    if (f.oneofGroup) {
      (oneofGroups[f.oneofGroup] ??= []).push(f);
    } else {
      plainFields.push(f);
    }
  });

  return { plainFields, oneofGroups };
};

/**
 * Formats request data for display in JSON format
 */
export const formatRequestForDisplay = (
  formData: Record<string, unknown>,
  schema?: FieldSchema[],
): string => {
  const requestObject = buildRequestObject(formData, schema);
  return JSON.stringify(requestObject, null, 2);
};
